import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import PageHeader from '../components/ui/PageHeader';
import { hasPermission, PERMISSIONS, handleAPIError } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function Funders() {
  const navigate = useNavigate();
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('list', '/dashboard');
  const [funders, setFunders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  // Set up unified header
  useEffect(() => {
    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'Funders',
      subtitle: null,
      actions: [
        hasPermission(PERMISSIONS.FUNDING_ENTITY_CREATE) && (
          <Link key="add" to="/funders/new" style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.2s ease',
          }}>
            + New Funder
          </Link>
        ),
      ].filter(Boolean),
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderConfig, resetHeaderConfig]);

  useEffect(() => {
    fetchFunders();
  }, []);

  const fetchFunders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/funding-entities`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFunders(response.data);
      setError(null);
    } catch (err) {
      const processedError = handleAPIError(err);
      setError(err);
      console.error('Failed to load funders:', processedError);
    } finally {
      setLoading(false);
    }
  };

  const filteredFunders = funders.filter(f =>
    (f.display_name?.toLowerCase().includes(filter.toLowerCase())) ||
    (f.legal_name?.toLowerCase().includes(filter.toLowerCase())) ||
    (f.email?.includes(filter))
  );

  const sortedFunders = [...filteredFunders].sort((a, b) =>
    (a.display_name || a.legal_name || '').localeCompare(b.display_name || b.legal_name || '')
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
          <p style={TYPOGRAPHY.BODY}>Loading funders...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        {error && (
        <div style={{
          padding: SPACING.LG,
          backgroundColor: '#fee2e2',
          border: `1px solid #fca5a5`,
          borderRadius: RADIUS.DEFAULT,
          color: COLORS.ERROR,
          marginBottom: SPACING.XL,
        }}>
          {error.message || 'Failed to load funders'}
        </div>
      )}

      {/* Page Header */}
      <PageHeader
        title="Funders"
        subtitle="Manage your funding entities"
        primaryAction={
          hasPermission(PERMISSIONS.FUNDING_ENTITY_CREATE) ? (
            <Link
              to="/funders/new"
              style={styles.primaryButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(77, 219, 148, 0.45)';
                e.currentTarget.style.backgroundColor = '#0a2c25';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(77, 219, 148, 0.3)';
                e.currentTarget.style.backgroundColor = COLORS.PRIMARY_DARK;
              }}
            >
              + New Funder
            </Link>
          ) : null
        }
      />

      {/* Search Bar */}
      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={styles.searchInput}
        />
        <span style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_LIGHT}}>
          Showing {sortedFunders.length} of {funders.length} funder{funders.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Content Area */}
      {filteredFunders.length === 0 ? (
        <div style={styles.card}>
          <div style={{
            textAlign: 'center',
            padding: SPACING.XXXL,
          }}>
            <h3 style={{...TYPOGRAPHY.H3, color: COLORS.TEXT_LIGHT, margin: 0, marginBottom: SPACING.MD}}>
              No funders found
            </h3>
            {filter ? (
              <p style={{...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, margin: 0, marginBottom: SPACING.LG}}>
                Try adjusting your search filters.
              </p>
            ) : (
              <>
                <p style={{...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, margin: 0, marginBottom: SPACING.LG}}>
                  Get started by creating your first funder.
                </p>
                <Link to="/funders/new" style={styles.primaryButton}>
                  Create Funder
                </Link>
              </>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.headerCell}>Funder Name</th>
                <th style={styles.headerCell}>Legal Name</th>
                <th style={styles.headerCell}>Email</th>
                <th style={styles.headerCell}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedFunders.map((funder, idx) => (
                <tr
                  key={funder.id}
                  style={{
                    ...styles.tableRow,
                    borderBottom: idx < sortedFunders.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.BG_SURFACE_SECONDARY}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{...styles.tableCell, fontWeight: '600', cursor: 'pointer'}} onClick={() => navigate(`/funders/${funder.id}`)}>
                    {funder.display_name || funder.legal_name || '—'}
                  </td>
                  <td style={styles.tableCell}>{funder.legal_name || '—'}</td>
                  <td style={styles.tableCell}>{funder.email || '—'}</td>
                  <td style={styles.tableCell}>
                    <button
                      onClick={() => navigate(`/funders/${funder.id}`)}
                      style={styles.editButton}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
  },

  container: {
    padding: `${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },

  primaryButton: {
    padding: `${SPACING.MD} ${SPACING.XL}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    textDecoration: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-block',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
    letterSpacing: '0.3px',
  },

  searchBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: SPACING.LG,
    marginBottom: SPACING.XL,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    borderRadius: RADIUS.DEFAULT,
  },

  searchInput: {
    flex: 1,
    maxWidth: '350px',
    padding: `${SPACING.SM} ${SPACING.MD}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    boxShadow: SHADOWS.DEFAULT,
    overflow: 'hidden',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  headerRow: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  headerCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    textAlign: 'left',
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  tableRow: {
    transition: 'background-color 0.2s ease',
  },

  tableCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    color: COLORS.TEXT_PRIMARY,
  },

  editButton: {
    padding: `${SPACING.XS} ${SPACING.SM}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};
